import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Input, Button, Alert, Badge } from 'reactstrap';

export default function VisaEligibilityCalculator() {
  const [visaType, setVisaType] = useState('');
  const [formData, setFormData] = useState({
    // Common
    age: '',
    nationality: '',

    // Visitor
    purpose: '',
    funds: '',
    ties: '',

    // Student
    coe: '',
    englishLevel: '',
    finances: '',
    genuineStudent: '',

    // Skilled - reuse from previous
    skilledAge: '',
    englishTest: '',
    englishScore: { listening: '', reading: '', writing: '', speaking: '' },
    skilledEmploymentAus: '',
    skilledEmploymentOverseas: '',
    educationLevel: '',
    // ... other skilled fields

    // Working Holiday
    whAge: '',
    whNationality: '',

    // Partner
    relationshipType: '',
    relationshipDuration: '',
    sponsorStatus: '',
    genuineRelationship: ''
  });

  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [details, setDetails] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateEligibility = () => {
    let result = { status: '', message: '', color: 'warning' };
    let det = [];
    let recs = [];

    if (visaType === 'visitor') {
      if (formData.funds === 'yes' && formData.ties === 'strong') {
        result = { status: 'Likely Eligible', message: 'You may qualify for eVisitor, ETA, or Visitor Visa (600).', color: 'success' };
        det.push('Genuine visitor intent required. Provide ties to home country.');
      } else {
        result.message = 'Strengthen ties to home country and proof of funds.';
        recs.push('Provide bank statements, employment letter, family ties.');
      }
    } else if (visaType === 'student') {
      if (formData.coe === 'yes' && formData.finances === 'yes' && formData.genuineStudent === 'yes') {
        result = { status: 'Likely Eligible for Student Visa (500)', message: 'Meet Genuine Student requirement and financials.', color: 'success' };
        det.push('2026 cap: 295,000 places. English: IELTS 6.0+ minimum.');
      } else {
        result.message = 'Ensure CoE, sufficient funds (~AUD 29,710+), and strong GS statement.';
        recs.push('Improve English if needed. Apply offshore if on visitor visa.');
      }
    } else if (visaType === 'skilled') {
      // Integrate simplified points calc from previous code
      // For brevity, assume points >=65
      result = { status: 'Check Points', message: 'Minimum 65 points for EOI. Use detailed calculator.', color: 'info' };
      recs.push('Consider state nomination for extra points.');
    } else if (visaType === 'working-holiday') {
      if (parseInt(formData.whAge) <= 30 && ['USA', 'Canada', 'UK', 'Ireland'].includes(formData.whNationality)) {
        result = { status: 'Eligible for WHV (417/462)', message: 'Depending on nationality.', color: 'success' };
      } else {
        result.message = 'Check eligible countries and age limit (up to 35 for some).';
      }
    } else if (visaType === 'partner') {
      if (formData.genuineRelationship === 'yes' && formData.sponsorStatus === 'eligible') {
        result = { status: 'Likely Eligible for Partner Visa (820/801 or 309/100)', message: 'Provide strong relationship evidence.', color: 'success' };
      } else {
        recs.push('Gather joint finances, photos, statements from friends.');
      }
    }

    setEligibilityResult(result);
    setDetails(det);
    setRecommendations(recs);
  };

  const handleReset = () => {
    setVisaType('');
    setFormData({
      age: '', nationality: '', purpose: '', funds: '', ties: '',
      coe: '', englishLevel: '', finances: '', genuineStudent: '',
      skilledAge: '', englishTest: '',
      englishScore: { listening: '', reading: '', writing: '', speaking: '' },
      skilledEmploymentAus: '', skilledEmploymentOverseas: '', educationLevel: '',
      whAge: '', whNationality: '',
      relationshipType: '', relationshipDuration: '', sponsorStatus: '', genuineRelationship: ''
    });
    setEligibilityResult(null);
    setDetails([]);
    setRecommendations([]);
  };

  return (
    <div style={{
      background: 'linear-gradient(180deg, #faf5fb 0%, #ffffff 100%)',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: '#7e1790',
        boxShadow: '0 2px 10px rgba(126, 23, 144, 0.15)'
      }}>
        <Container>
          <div className="py-4">
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <span style={{ fontSize: '2.5rem', marginRight: '1rem' }}>🇦🇺</span>
                  <div>
                    <h1 className="text-white mb-1 fw-bold" style={{
                      fontSize: '1.75rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Australian Visa Eligibility Calculator
                    </h1>
                    <p className="text-white mb-0" style={{
                      fontSize: '0.9rem',
                      opacity: 0.9
                    }}>
                      Quick check for popular visas
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="py-5">
        <Row>
          {/* Main Form Section */}
          <Col lg={8} className="mb-4">
            <Card className="border-0 shadow-sm" style={{
              borderRadius: '16px',
              overflow: 'hidden'
            }}>
              <CardHeader className="bg-white border-0 p-4" style={{
                borderBottom: '3px solid #7e1790'
              }}>
                <FormGroup className="mb-0">
                  <Label for="visaType" className="mb-3 fw-bold" style={{
                    fontSize: '1.25rem',
                    color: '#1a1a1a'
                  }}>
                    Select Visa Type
                  </Label>
                  <Input
                    type="select"
                    name="visaType"
                    id="visaType"
                    value={visaType}
                    onChange={(e) => setVisaType(e.target.value)}
                    className="form-select form-select-lg"
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      fontSize: '1rem',
                      padding: '0.75rem 1rem'
                    }}
                  >
                    <option value="">Choose your visa type...</option>
                    <option value="visitor">Visitor / Tourist (eVisitor/ETA/600)</option>
                    <option value="student">Student Visa (Subclass 500)</option>
                    <option value="skilled">Skilled Migration (189/190/491)</option>
                    <option value="partner">Partner / Spouse Visa</option>
                    <option value="working-holiday">Working Holiday (417/462)</option>
                  </Input>
                </FormGroup>
              </CardHeader>

              <CardBody className="p-4 p-md-5">
                {visaType && (
                  <>
                    {/* Visitor Visa Form */}
                    {visaType === 'visitor' && (
                      <>
                        <h5 className="fw-bold mb-4 pb-2" style={{
                          color: '#7e1790',
                          fontSize: '1.25rem',
                          borderBottom: '2px solid #f3f4f6'
                        }}>
                          Visitor Visa Requirements
                        </h5>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Nationality (for ETA/eVisitor eligibility)
                          </Label>
                          <Input
                            type="text"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            placeholder="e.g., United States, United Kingdom"
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          />
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Sufficient Funds?
                          </Label>
                          <Input
                            type="select"
                            name="funds"
                            value={formData.funds}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </Input>
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Strong Ties to Home Country?
                          </Label>
                          <Input
                            type="select"
                            name="ties"
                            value={formData.ties}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="strong">Strong</option>
                            <option value="moderate">Moderate</option>
                            <option value="weak">Weak</option>
                          </Input>
                        </FormGroup>
                      </>
                    )}

                    {/* Student Visa Form */}
                    {visaType === 'student' && (
                      <>
                        <h5 className="fw-bold mb-4 pb-2" style={{
                          color: '#7e1790',
                          fontSize: '1.25rem',
                          borderBottom: '2px solid #f3f4f6'
                        }}>
                          Student Visa Requirements
                        </h5>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Have Confirmation of Enrolment (CoE)?
                          </Label>
                          <Input
                            type="select"
                            name="coe"
                            value={formData.coe}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </Input>
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            English Proficiency (IELTS 6.0+)?
                          </Label>
                          <Input
                            type="select"
                            name="englishLevel"
                            value={formData.englishLevel}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </Input>
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Sufficient Finances?
                          </Label>
                          <Input
                            type="select"
                            name="finances"
                            value={formData.finances}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </Input>
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Genuine Student Intent?
                          </Label>
                          <Input
                            type="select"
                            name="genuineStudent"
                            value={formData.genuineStudent}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </Input>
                        </FormGroup>
                      </>
                    )}

                    {/* Skilled Migration */}
                    {visaType === 'skilled' && (
                      <>
                        <h5 className="fw-bold mb-4 pb-2" style={{
                          // color: '#7e1790',
                          // fontSize: '1.25rem',
                          // borderBottom: '2px solid #f3f4f6'
                        }}>
                          Skilled Migration Overview
                        </h5>

                        <Alert color="light" className="border-0" style={{
                          background: 'linear-gradient(135deg, rgba(126, 23, 144, 0.05) 0%, rgba(126, 23, 144, 0.02) 100%)',
                          borderRadius: '12px',
                          borderLeft: '4px solid #7e1790'
                        }}>
                          <h6 className="fw-bold mb-3" style={{ color: '#7e1790' }}>Key Requirements:</h6>
                          <ul className="mb-0" style={{ lineHeight: '1.8' }}>
                            <li>Occupation on skilled occupation list</li>
                            <li>Minimum 65 points (use detailed calculator)</li>
                            <li>Skills assessment for nominated occupation</li>
                            <li>Competent English (IELTS 6+ or equivalent)</li>
                            <li>Age under 45 at time of invitation</li>
                          </ul>
                        </Alert>

                        <Alert color="info" className="d-flex align-items-start border-0" style={{
                          background: '#e0f2fe',
                          borderRadius: '12px',
                          borderLeft: '4px solid #0ea5e9'
                        }}>
                          <span className="me-3" style={{ fontSize: '1.5rem' }}>💡</span>
                          <div>
                            <strong>Recommendation:</strong> Use the official SkillSelect points calculator for accurate assessment. Consider state nomination (190/491) for additional points.
                          </div>
                        </Alert>
                      </>
                    )}

                    {/* Working Holiday */}
                    {visaType === 'working-holiday' && (
                      <>
                        <h5 className="fw-bold mb-4 pb-2" style={{
                          color: '#7e1790',
                          fontSize: '1.25rem',
                          borderBottom: '2px solid #f3f4f6'
                        }}>
                          Working Holiday Visa
                        </h5>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Your Age
                          </Label>
                          <Input
                            type="number"
                            name="whAge"
                            value={formData.whAge}
                            onChange={handleChange}
                            placeholder="Enter your age"
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          />
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Nationality
                          </Label>
                          <Input
                            type="select"
                            name="whNationality"
                            value={formData.whNationality}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select your country...</option>
                            <option value="USA">United States</option>
                            <option value="Canada">Canada</option>
                            <option value="UK">United Kingdom</option>
                            <option value="Ireland">Ireland</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Italy">Italy</option>
                            <option value="Japan">Japan</option>
                            <option value="South Korea">South Korea</option>
                          </Input>
                        </FormGroup>
                      </>
                    )}

                    {/* Partner Visa */}
                    {visaType === 'partner' && (
                      <>
                        <h5 className="fw-bold mb-4 pb-2" style={{
                          color: '#7e1790',
                          fontSize: '1.25rem',
                          borderBottom: '2px solid #f3f4f6'
                        }}>
                          Partner Visa Requirements
                        </h5>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Is your sponsor an Australian citizen or PR holder?
                          </Label>
                          <Input
                            type="select"
                            name="sponsorStatus"
                            value={formData.sponsorStatus}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="eligible">Yes, eligible to sponsor</option>
                            <option value="not-eligible">No or unsure</option>
                          </Input>
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                            Can you prove a genuine and continuing relationship?
                          </Label>
                          <Input
                            type="select"
                            name="genuineRelationship"
                            value={formData.genuineRelationship}
                            onChange={handleChange}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid #e5e7eb',
                              padding: '0.75rem 1rem',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes, with strong evidence</option>
                            <option value="no">Need to gather evidence</option>
                          </Input>
                        </FormGroup>
                      </>
                    )}

                    {/* Action Buttons */}
                    <div className="d-grid gap-3 mt-4 pt-4" style={{ borderTop: '2px solid #f3f4f6' }}>
                      <Button
                        size="lg"
                        onClick={calculateEligibility}
                        className="fw-bold"
                        style={{
                          background: '#7e1790',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '0.875rem',
                          fontSize: '1.1rem',
                          boxShadow: '0 4px 12px rgba(126, 23, 144, 0.25)'
                        }}
                      >
                        Check Eligibility 🎯
                      </Button>
                      <Button
                        color="light"
                        size="lg"
                        onClick={handleReset}
                        style={{
                          borderRadius: '10px',
                          padding: '0.875rem',
                          fontSize: '1rem',
                          border: '2px solid #e5e7eb'
                        }}
                      >
                        Reset Calculator
                      </Button>
                    </div>
                  </>
                )}

                {/* Empty State */}
                {!visaType && (
                  <div className="text-center py-5">
                    <div style={{ fontSize: '4rem', opacity: 0.3, marginBottom: '1rem' }}>📋</div>
                    <h4 className="fw-bold mb-2" style={{ color: '#374151' }}>Get Started</h4>
                    <p className="text-muted mb-0">Select a visa type above to begin your eligibility assessment</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Results Sidebar */}
          <Col lg={4}>
            <div style={{ position: 'sticky', top: '20px' }}>
              <Card className="border-0 shadow-sm mb-3" style={{
                borderRadius: '16px',
                overflow: 'hidden'
              }}>
                <div className="text-white text-center py-4" style={{
                  background: 'linear-gradient(135deg, #7e1790 0%, #a020b0 100%)'
                }}>
                  <h2 className="fw-bold mb-0" style={{ fontSize: '1.5rem' }}>Eligibility Result</h2>
                </div>

                <CardBody className="p-4">
                  {eligibilityResult ? (
                    <>
                      <Alert color={eligibilityResult.color} className="border-0" style={{ borderRadius: '10px' }}>
                        <h5 className="fw-bold mb-2">
                          {eligibilityResult.status}
                        </h5>
                        <p className="mb-0">{eligibilityResult.message}</p>
                      </Alert>

                      {details.length > 0 && (
                        <div className="mt-3">
                          {details.map((d, i) => (
                            <div
                              key={i}
                              className="mb-2 p-3"
                              style={{
                                background: '#faf5fb',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                borderLeft: '3px solid #7e1790'
                              }}
                            >
                              {d}
                            </div>
                          ))}
                        </div>
                      )}

                      {recommendations.length > 0 && (
                        <>
                          <h6 className="fw-bold mt-4 mb-3" style={{ color: '#7e1790' }}>
                            💡 Recommendations
                          </h6>
                          {recommendations.map((r, i) => (
                            <Alert key={i} color="warning" className="border-0 mb-2" style={{ borderRadius: '8px' }}>
                              <small>{r}</small>
                            </Alert>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <div style={{ fontSize: '3.5rem', opacity: 0.3, marginBottom: '1rem' }}> </div>
                      <p className="text-muted mb-0">Select a visa type and answer the questions to check your eligibility.</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Disclaimer */}
              <Card className="border-0" style={{
                background: 'linear-gradient(135deg, #fff9e6 0%, #fef3c7 100%)',
                borderRadius: '12px'
              }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-start">
                    <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>⚠️</span>
                    <small style={{ color: '#92400e', lineHeight: '1.5' }}>
                      <strong className="fw-bold">Disclaimer:</strong> This is a general guide only. Visa rules can change. Consult the Department of Home Affairs or a registered MARA agent for official advice.
                    </small>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}